/**
 * An archived item in the panel beside the results: its preview copy to watch (with IN and OUT to ask for only a part
 * of the file back), or the tape it is on when it has none (archived before previews were kept), with a video to make
 * one from, dropped or chosen, for whoever may change the item; its tape, its file as it was written to tape, who made
 * its preview and from what, its catalogue fields; a restore asked from here, and how the item's restores went.
 */
import { useCallback, useEffect, useRef, useState, type DragEvent, type FormEvent } from 'react';
import { formatBytes, type ArchiveMediaInfo, type PreviewSource } from '@airpost/shared';
import { ApiError, get, post } from '../api.js';
import { isRunning, startPreview, usePreviewJobs } from '../archive-preview.js';
import { ElementLevels } from '../element-levels.js';
import { t } from '../i18n.js';
import { JobProgress, VIDEO_ACCEPT, droppedFile, pickProblem, readPicked, type ServerIngest } from './archive-ingest.js';
import { Cartridge } from '../tape-robot.js';
import { fmtDateInput, fmtDateTime, hhmmss } from '../util.js';
import { VuMeter } from '../vu-meter.js';
import { RestoreRow, tcOf, type RestoreDto } from './archive-restores.js';
import type { TapeDto } from './archive-tapes.js';
import type { VideoDto } from './videos.js';

interface ItemDetail {
  file: { name: string; size: number | null; checksum: string | null; tapePath: string | null; media: ArchiveMediaInfo | null; archivedAt: string | null } | null;
  proxy: { size: number; durationMs: number | null; width: number | null; height: number | null; hasThumb: boolean; createdAt: string; updatedAt: string; source: PreviewSource; sourceName: string | null; sourceSize: number | null; madeBy: string | null } | null;
  tape: TapeDto | null;
  restores: RestoreDto[];
  targets: Array<{ key: string; label: string }>;
  canRestore: boolean;
  /** This user may change the item, and so give it a preview. */
  canPreview: boolean;
}

export const locationLabel = (l: string): string => ({ library: t('ltoLocLibrary'), drive: t('ltoLocDrive'), shelf: t('ltoLocShelf'), offsite: t('ltoLocOffsite'), unknown: t('ltoLocUnknown') })[l] ?? l;

const mediaLine = (m: ArchiveMediaInfo | null): string =>
  m
    ? [m.container?.toUpperCase(), m.videoCodec, m.width && m.height ? `${m.width}×${m.height}` : null, m.fps ? `${m.fps}${m.scan === 'interlaced' ? 'i' : 'p'}` : null, m.bitrate ? `${Math.round(m.bitrate / 1e6)} Mb/s` : null, m.audioCodec ? `${m.audioCodec}${m.audioChannels ? ` ×${m.audioChannels}` : ''}` : null].filter(Boolean).join(' · ')
    : '—';

const sourceLabel = (s: PreviewSource): string => ({ agent: t('ltoSrcAgent'), browser: t('ltoSrcBrowser'), server: t('ltoSrcServer') })[s] ?? s;

export function ItemPanel({ video, ingest, onEdit, onDelete, onOpenTape, onRestoresChanged }: { video: VideoDto; ingest: ServerIngest | null; onEdit: () => void; onDelete: () => void; onOpenTape: (tapeId: string) => void; onRestoresChanged: () => void }) {
  const [d, setD] = useState<ItemDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nowMs, setNowMs] = useState(0);
  const [mark, setMark] = useState<{ in: number | null; out: number | null }>({ in: null, out: null });
  const [target, setTarget] = useState('');
  const [part, setPart] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const player = useRef<HTMLVideoElement | null>(null);
  // the preview's sound, for the VU meter: woken by a touch, a key or a play on the picture
  const [levels] = useState(() => new ElementLevels());
  useEffect(() => () => levels.close(), [levels]);
  const wakeMeter = () => levels.wake(player.current);
  // a preview being made for the item; a video dropped or chosen here to make one
  const job = usePreviewJobs().find((j) => j.videoId === video.id && isRunning(j)) ?? null;
  const pickInput = useRef<HTMLInputElement | null>(null);
  const [pickNote, setPickNote] = useState<string | null>(null);
  const [over, setOver] = useState(false);
  const takeFile = (file: File | null) => {
    if (!file || !d?.canPreview) return;
    setPickNote(t('ltoPickLooking'));
    readPicked(file, (p) => {
      const problem = pickProblem(p, ingest);
      setPickNote(problem);
      if (!problem && p.look) startPreview(video.id, video.title, p.file, p.look, !!ingest?.server);
    });
  };
  const dropping = d?.canPreview
    ? {
        onDragOver: (e: DragEvent) => {
          e.preventDefault();
          setOver(true);
        },
        onDragLeave: () => setOver(false),
        onDrop: (e: DragEvent) => {
          e.preventDefault();
          setOver(false);
          takeFile(droppedFile(e));
        },
      }
    : {};

  const load = useCallback(() => {
    get<ItemDetail>(`/api/archive/items/${video.id}`)
      .then((x) => {
        setD(x);
        setError(null);
        setTarget((cur) => (x.targets.some((tg) => tg.key === cur) ? cur : (x.targets[0]?.key ?? '')));
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)));
  }, [video.id]);
  // the panel is keyed by the item: a new item starts afresh
  useEffect(() => load(), [load]);
  // while a restore of the item is under way, follow it
  const openRestore = d?.restores.some((r) => r.status === 'requested' || r.status === 'loading' || r.status === 'reading') ?? false;
  useEffect(() => {
    if (!openRestore) return;
    const h = window.setInterval(load, 3000);
    return () => window.clearInterval(h);
  }, [openRestore, load]);

  const place = (next: { in: number | null; out: number | null }) => {
    setMark(next);
    // a whole range marked: the restore is of that part (the journalist can still choose the whole file)
    if (next.in !== null && next.out !== null && next.out > next.in) setPart(true);
  };
  const setIn = () => place({ in: nowMs, out: mark.out !== null && mark.out <= nowMs ? null : mark.out });
  const setOut = () => place({ in: mark.in !== null && mark.in >= nowMs ? null : mark.in, out: nowMs });
  const hasRange = mark.in !== null && mark.out !== null && mark.out > mark.in;
  const onKey = (e: React.KeyboardEvent) => {
    if ((e.target as HTMLElement).closest('input, textarea, select')) return;
    if (e.key === 'i' || e.key === 'I') setIn();
    else if (e.key === 'o' || e.key === 'O') setOut();
  };

  const ask = async (e: FormEvent) => {
    e.preventDefault();
    if (!target) return;
    setBusy(true);
    setError(null);
    try {
      const body = { videoId: video.id, target, reason: reason.trim() || null, ...(part && hasRange ? { inMs: mark.in, outMs: mark.out } : {}) };
      const r = await post<{ restore: RestoreDto; existing: boolean }>('/api/archive/restores', body);
      setNotice(r.existing ? t('ltoRestoreExisting') : t('ltoRestoreAsked'));
      setReason('');
      load();
      onRestoresChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  // the preview's address changes with each new preview (the browser keeps one an hour)
  const ver = d?.proxy ? `?v=${Date.parse(d.proxy.updatedAt)}` : '';
  return (
    <aside className="lto-panel" tabIndex={-1} onKeyDown={onKey} aria-label={video.title}>
      <input
        ref={pickInput}
        type="file"
        accept={VIDEO_ACCEPT}
        hidden
        onChange={(e) => {
          takeFile(e.target.files?.[0] ?? null);
          e.target.value = '';
        }}
      />
      <div className={`lto-stage${over ? ' over' : ''}`} {...dropping}>
        {!d ? (
          <div className="lto-stage-wait" />
        ) : d.proxy ? (
          <>
            <div className="lto-screen" onPointerDown={wakeMeter} onKeyDown={wakeMeter}>
              <video
                key={ver}
                ref={player}
                className="lto-player"
                controls
                preload="metadata"
                playsInline
                poster={d.proxy.hasThumb ? `/api/archive/items/${video.id}/thumb${ver}` : undefined}
                src={`/api/archive/items/${video.id}/proxy${ver}`}
                onPlay={wakeMeter}
                onTimeUpdate={(e) => setNowMs(Math.round(e.currentTarget.currentTime * 1000))}
                onSeeked={(e) => setNowMs(Math.round(e.currentTarget.currentTime * 1000))}
              />
              <VuMeter levels={levels} reset={`${video.id}${ver}`} className="lto-vu" />
            </div>
            <div className="lto-transport">
              <span className="lto-tc">{tcOf(nowMs)}</span>
              <button type="button" className="lto-btn sm" title="I" onClick={setIn}>
                ⟦ IN
              </button>
              <button type="button" className="lto-btn sm" title="O" onClick={setOut}>
                OUT ⟧
              </button>
              <span className={`lto-range${hasRange ? ' on' : ''}`}>
                {mark.in !== null ? tcOf(mark.in) : '--:--:--:--'} → {mark.out !== null ? tcOf(mark.out) : '--:--:--:--'}
              </span>
              {(mark.in !== null || mark.out !== null) && (
                <button type="button" className="lto-btn sm ghost" onClick={() => setMark({ in: null, out: null })}>
                  ✕
                </button>
              )}
              <span className="lto-proxy-badge" title={t('ltoProxyHint')}>
                {t('ltoProxyBadge')}
                {d.proxy.width ? ` ${d.proxy.width}×${d.proxy.height}` : ''}
              </span>
            </div>
            {job && (
              <div className="lto-stage-job">
                <JobProgress job={job} />
              </div>
            )}
          </>
        ) : (
          <div className="lto-noproxy">
            <Cartridge barcode={video.tapeNo ?? '—'} generation={d.tape?.generation} size="lg" />
            <div>
              <b>{t('ltoNoProxy')}</b>
              {job ? (
                <div className="lto-stage-job">
                  <JobProgress job={job} />
                </div>
              ) : (
                <p>{video.tapeNo ? t('ltoNoProxyHint') : t('ltoNoTapeHint')}</p>
              )}
              {d.canPreview && !job && (
                <div className="lto-add-preview">
                  <button type="button" className="lto-btn" onClick={() => pickInput.current?.click()}>
                    ⬆ {t('ltoAddPreview')}
                  </button>
                  <small>{t('ltoAddPreviewHint')}</small>
                </div>
              )}
              {pickNote && !job && <p className="lto-hint">{pickNote}</p>}
            </div>
          </div>
        )}
      </div>

      <div className="lto-panel-body">
        <h2 className="lto-item-title">{video.title}</h2>
        <div className="lto-item-sub">
          {fmtDateInput(video.eventDate)}
          {video.videoTypeLabel && <span className={`lto-type ${video.videoType ?? ''}`}>{video.videoTypeLabel}</span>}
          <span>{hhmmss(video.durationS)}</span>
          {video.categories.map((c) => (
            <span key={c.id} className="lto-cat">
              {c.name}
            </span>
          ))}
        </div>
        <div className="lto-actions">
          {video.canEdit && (
            <button type="button" className="lto-btn" onClick={onEdit}>
              ✎ {t('editRecord')}
            </button>
          )}
          {!video.canEdit && (
            <button type="button" className="lto-btn" onClick={onEdit}>
              ☰ {t('ltoCard')}
            </button>
          )}
          {video.canDelete && (
            <button type="button" className="lto-btn ghost danger" onClick={onDelete}>
              🗑 {t('delete')}
            </button>
          )}
        </div>

        {error && <div className="lto-err">{error}</div>}

        <dl className="lto-facts">
          <dt>{t('ltoTape')}</dt>
          <dd>
            {d?.tape ? (
              <button type="button" className="lto-link" onClick={() => onOpenTape(d.tape!.id)}>
                <b className="lto-barcode-chip">{d.tape.barcode}</b> {d.tape.generation ?? ''} · {locationLabel(d.tape.location)}
                {d.tape.slot ? ` ${d.tape.slot}` : ''}
              </button>
            ) : (
              (video.tapeNo ?? '—')
            )}
            {video.itemNo !== null && (
              <span className="muted">
                {' '}
                · {t('itemNo')} {video.itemNo}
              </span>
            )}
          </dd>
          <dt>TC</dt>
          <dd>
            {video.tcIn} → {video.tcOut}
          </dd>
          {d?.proxy && (
            <>
              <dt>{t('ltoPreviewFrom')}</dt>
              <dd>
                {sourceLabel(d.proxy.source)}
                {d.proxy.sourceName && (
                  <>
                    {' · '}
                    <span className="lto-mono">{d.proxy.sourceName}</span>
                    {d.proxy.sourceSize ? ` (${formatBytes(d.proxy.sourceSize)})` : ''}
                  </>
                )}
                {' · '}
                {formatBytes(d.proxy.size)}
                <span className="muted">
                  {' · '}
                  {d.proxy.madeBy ? `${d.proxy.madeBy} · ` : ''}
                  {fmtDateTime(d.proxy.updatedAt)}
                </span>
              </dd>
            </>
          )}
          {d?.file && (
            <>
              <dt>{t('ltoFile')}</dt>
              <dd>
                <span className="lto-mono">{d.file.name}</span> · {formatBytes(d.file.size)}
              </dd>
              <dt>{t('ltoFormat')}</dt>
              <dd>{mediaLine(d.file.media)}</dd>
              <dt>{t('ltoOnTape')}</dt>
              <dd>
                <span className="lto-mono">{d.file.tapePath}</span>
                {d.file.archivedAt && <span className="muted"> · {fmtDateTime(d.file.archivedAt)}</span>}
              </dd>
              <dt>SHA-256</dt>
              <dd className="lto-mono small" title={d.file.checksum ?? ''}>
                {d.file.checksum ? `${d.file.checksum.slice(7, 23)}…` : '—'}
              </dd>
            </>
          )}
          {video.comments && (
            <>
              <dt>{t('comments')}</dt>
              <dd>{video.comments}</dd>
            </>
          )}
        </dl>
        {video.summary && <div className="lto-summary">{video.summary}</div>}

        {d?.canRestore && (
          <form className="lto-restore-form" onSubmit={ask}>
            <div className="lto-form-title">⟲ {t('ltoRestoreTitle')}</div>
            <div className="lto-form-row">
              <label>
                {t('ltoRestoreTo')}
                <select value={target} onChange={(e) => setTarget(e.target.value)}>
                  {d.targets.map((tg) => (
                    <option key={tg.key} value={tg.key}>
                      {tg.label}
                    </option>
                  ))}
                </select>
              </label>
              <div className="lto-seg small" role="radiogroup">
                <button type="button" role="radio" aria-checked={!part} className={!part ? 'on' : ''} onClick={() => setPart(false)}>
                  {t('ltoWholeFile')}
                </button>
                <button type="button" role="radio" aria-checked={part} className={part ? 'on' : ''} disabled={!hasRange} title={hasRange ? '' : t('ltoPartHint')} onClick={() => setPart(true)}>
                  {t('ltoPart')} {hasRange ? `${tcOf(mark.in!)} → ${tcOf(mark.out!)}` : ''}
                </button>
              </div>
            </div>
            <input type="text" className="lto-input" maxLength={500} placeholder={t('ltoReason')} value={reason} onChange={(e) => setReason(e.target.value)} />
            {d.targets[0]?.key === 'manual' && <p className="lto-hint">{d.file ? t('ltoManualAgentOff') : t('ltoManualHint')}</p>}
            <button type="submit" className="lto-btn primary" disabled={busy || !target}>
              {t('ltoRestoreAsk')}
            </button>
            {notice && <div className="lto-ok">{notice}</div>}
          </form>
        )}

        {d && d.restores.length > 0 && (
          <div className="lto-item-restores">
            <div className="lto-form-title">{t('ltoItemRestores')}</div>
            {d.restores.map((r) => (
              <RestoreRow key={r.id} r={r} showTitle={false} onChanged={() => load()} />
            ))}
          </div>
        )}
      </div>
    </aside>
  );
}
